import axios from 'axios';
import Pagination from '@/domain/pagination.entity';
import Response from '@/domain/response.entity';
import Contract from '../entity/contract.entity';
import BindInstance from '../entity/bind-instance.entity';
import ContractInstance from '../entity/contract-instance.entity';
import EditInstanceRequest from '../request/edit-instance-request.entity';
import InstanceFilter from '../entity/instance-filter.entity';
import UnboundInstance from '../entity/unbound-instance.entity';
import InstanceSummary from '../entity/instances-summary.entity';
import InstanceJustificationAdd from '../entity/instance-justification-add.entity';

export default class instanceService {
  public static bindInstance(businessUnitId: string, contractId: string, bindInstance: BindInstance)
    : Promise<Contract> {
    return axios.post(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances`, bindInstance)
      .then((response: any) => Contract.of(response.data))
  }

  public static unbindInstance(businessUnitId: string, contractId: string, instanceCode: string)
    : Promise<Contract> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/${instanceCode}/inactivate`)
      .then((response: any) => Contract.of(response.data))
  }

  public static activateInstance(businessUnitId: string, contractId: string,
    activateInstance: BindInstance)
    : Promise<ContractInstance> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/
    ${contractId}/instances/${activateInstance.id}/activate`, activateInstance)
      .then((response: any) => ContractInstance.of(response.data))
  }

  public static deleteInstance(
    businessUnitId: string,
    contractId: string,
    instanceId: string,
  ): Promise<void> {
    return axios
      .delete(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/${instanceId}`);
  }

  public static editInstance(
    businessUnitId: string,
    contractId: string,
    instanceId: string,
    editInstanceRequest: EditInstanceRequest,
  ): Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/${instanceId}`, editInstanceRequest);
  }

  public static getDateChangeImpact(
    businessUnitId: string,
    contractId: string,
    instanceId: string,
    startedAt: string,
  ): Promise<{ competence: string; hasInvoiceEmitted: boolean }[]> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/${instanceId}/date-change-impact`, {
        params: { startedAt },
      })
      .then((response: any) => response.data);
  }

  public static getUnboundInstances(
    pagination: Pagination,
    businessUnitId: string,
    filter: InstanceFilter,
  ): Promise<Response> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/instances/unbound`, {
        params: {
          page: pagination.page - 1,
          size: pagination.itemsPerPage,
          sortBy: pagination.arrangeSortBy,
          code: filter.code,
          description: filter.description,
          status: filter.status,
          competence: filter.competence,
        },
      })
      .then((response: any) => Response.of(response.data));
  }

  public static inactivateInstance(businessUnitId: string, unboundInstances: UnboundInstance[])
  : Promise<void> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/instances/inactivate`, unboundInstances);
  }

  public static transferInstances(businessUnitId: string,
    destinationBusinessUnitId: string, unboundInstances: UnboundInstance[])
  : Promise<void> {
    const updateData = {
      origin: businessUnitId,
      destination: destinationBusinessUnitId,
      unboundInstances,
    };
    return axios.put(`/api/v1/business-units/${businessUnitId}/instances/trasnfer`, updateData);
  }

  public static getInstanceSummary(
    businessUnitId: string,
    competence: string,
  ): Promise<InstanceSummary> {
    return axios.get(`/api/v1/business-units/${businessUnitId}/instances/summary?competence=${competence}`)
      .then((response) => new InstanceSummary(response.data))
  }

  public static addJustification(
    instanceIntegrationsIds: Array<string>,
    justification: InstanceJustificationAdd,
  ) {
    return axios.put('/api/v1/instances/justifications', {
      instanceIntegrationsIds,
      justificationId: justification.justificationId,
      observation: justification.observation,
    })
  }

  public static reprocessInstances(businessUnitId: string, contractId: string): Promise<void> {
    return axios.post(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/reprocess`);
  }
}
